import { Row, Col } from 'reactstrap';
import { ColumnDef } from '@tanstack/react-table';
import classNames from 'classnames';
import {
    NSCard,
    NSLabel, NSSelect,
} from 'bricks';
import { format, parseISO } from 'date-fns';
import NSInputNumberStepper from 'bricks/NSInputNumberStepper/NSInputNumberStepper';
import './ProFormaPermanentDebt.scss';
import RenderIf from 'components/RenderIf/RenderIf';
import { IPatchOperatingInfoData, PermanentDebtSizingMethod } from 'views/ProFormaTable/types';
import { numeralFormatterCurrency, percentFormatter } from 'ns_libs/formatter';
import { Periods } from 'components/ProFormaReturnsTable/constant';
import { IOptionAnyValue } from 'bricks/types';
import { CALCULATION_METHOD_OPTIONS, INTEREST_OPTIONS } from './constants';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import DebtServiceTable from './DebtServiceTable/DebtServiceTable';
import MetricOutputCard from '../../../MetricOutputCard/MetricOutputCard';
import BaseTotalsTable from '../BaseTotalsTable/BaseTotalsTable';
import { useCustomScroll } from '../../../../hooks/useCustomScroll';

export interface IProFormaPermanentDebtProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const ProFormaPermanentDebt = ({
    operationBeginIndex, baseCashFlowColumns,
}: IProFormaPermanentDebtProps) => {
    const {
        operatingInfo, handleUpdateProFormaOperations, showCents, periodCadence, cashFlowPeriods,
    } = useProFormaSocketContext();

    const {
        tableRef, handleTableScroll, createRefHandler,
    } = useCustomScroll('permanent-debt-table', ['income-table', 'expenses-table']);

    const updateOperationsKeys = (keyValues: Partial<IPatchOperatingInfoData>) => {
        if (operatingInfo?.id) {
            handleUpdateProFormaOperations(operatingInfo.id, keyValues);
        }
    };

    let cashFlowAfterFinancingPeriods = operatingInfo?.cashFlowAfterFinancing?.cashFlow || [];
    if (periodCadence === Periods.ANNUALLY) cashFlowAfterFinancingPeriods = operatingInfo?.cashFlowAfterFinancing?.yearlyCashFlow || [];
    const cashFlowAfterFinancingValues = cashFlowAfterFinancingPeriods.map(period => period.value);

    const stabilizedMonth = operatingInfo.stabilizedMonth ?? 0;
    const loanFundingPeriods = cashFlowPeriods.slice(stabilizedMonth);

    const loanFundingDatePlaceholder = `${operatingInfo?.loanFundingDate
        ? `${format(parseISO(operatingInfo?.loanFundingDate), 'MMM yyyy')} (Month ${operatingInfo.loanFundingMonth})` : ''}`;

    const loanFundingDateOptions = loanFundingPeriods.map((periodDate, index) => ({
        label: `${format(parseISO(periodDate), 'MMM yyyy')} (Month ${stabilizedMonth + index})`,
        value: periodDate,
    }));

    const handleLoanFundingOptionSelect = (option: IOptionAnyValue) => {
        if (operatingInfo?.id) {
            handleUpdateProFormaOperations(operatingInfo.id, { permanentDebt: { loanFundingDate: option.value } });
        }
    };

    return (
        <div className="ProFormaPermanentDebt">
            <Row className="mx-n1 pb-3 px-3">
                <Col className="px-1 pb-2" sm={12} md={6}>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="pl-0 col-5 my-auto font-weight-normal text-dark text-nowrap">Loan funding</NSLabel>
                        <NSSelect
                            name="loanFundingDate"
                            options={loanFundingDateOptions}
                            value={loanFundingDateOptions.find(option => {
                                if (!operatingInfo?.loanFundingDate) return false;
                                const optDate = parseISO(option.value);
                                const infoDate = parseISO(operatingInfo.loanFundingDate);
                                return optDate.getFullYear() === infoDate.getFullYear()
                                    && optDate.getMonth() === infoDate.getMonth();
                            })}
                            onChange={handleLoanFundingOptionSelect}
                            placeholder={loanFundingDatePlaceholder}
                            isClearable={false}
                            isSearchable
                            menuPortalTarget={document.body}
                            formClassName="ProFormaPermanentDebt__select-width"
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="pl-0 col-5 my-auto font-weight-normal text-dark text-nowrap">Interest only?</NSLabel>
                        <NSSelect
                            options={INTEREST_OPTIONS}
                            name="isInterestOnly"
                            value={INTEREST_OPTIONS.find(option => operatingInfo.isInterestOnly === option.value)}
                            onChange={option => updateOperationsKeys({ permanentDebt: { isInterestOnly: option.value } })}
                            isClearable={false}
                            formClassName="ProFormaPermanentDebt__input-width"
                        />
                    </div>

                    <RenderIf isTrue={!operatingInfo.isInterestOnly}>
                        <div className="d-flex align-items-center mt-2">
                            <NSLabel className="pl-0 col-5 font-weight-normal text-dark text-nowrap">Amortization</NSLabel>
                            <NSInputNumberStepper
                                name="amortizationInYears"
                                value={operatingInfo.amortizationInYears || null}
                                onChange={(value: number) => updateOperationsKeys({ permanentDebt: { amortizationInYears: value } })}
                                min={0}
                                step={1}
                                customSuffix="years"
                                inputGroupClassName="ProFormaPermanentDebt__input-width"
                            />
                        </div>
                    </RenderIf>

                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="pl-0 col-5 font-weight-normal text-dark text-nowrap">APR</NSLabel>
                        <NSInputNumberStepper
                            name="annualPercentageRate"
                            value={operatingInfo.annualPercentageRate || null}
                            onChange={(value: number) => updateOperationsKeys({ permanentDebt: { annualPercentageRate: value } })}
                            min={0}
                            step={0.1}
                            max={100}
                            percentage
                            inputGroupClassName="ProFormaPermanentDebt__input-width"
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="pl-0 col-5 font-weight-normal text-dark text-nowrap">Fees & closing costs</NSLabel>
                        <NSInputNumberStepper
                            name="feesAndClosingCostsPercentage"
                            value={operatingInfo.feesAndClosingCostsPercentage || null}
                            onChange={(value: number) => updateOperationsKeys({ permanentDebt: { feesAndClosingCostsPercentage: value } })}
                            min={0}
                            step={0.1}
                            max={100}
                            percentage
                            inputGroupClassName="ProFormaPermanentDebt__input-width"
                        />
                    </div>
                    <hr className="mt-2 my-1 ProFormaPermanentDebt__form-width" />
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className="pl-0 col-5 font-weight-normal text-dark text-nowrap">
                            Calculation method
                        </NSLabel>
                        <NSSelect
                            options={CALCULATION_METHOD_OPTIONS}
                            name="permanentDebtSizingMethod"
                            value={CALCULATION_METHOD_OPTIONS.find(
                                option => (operatingInfo.permanentDebtSizingMethod || 'default') === option.value
                            )}
                            onChange={option => updateOperationsKeys({ permanentDebt: { permanentDebtSizingMethod: option.value } })}
                            isClearable={false}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            formClassName="ProFormaPermanentDebt__input-width"
                        />
                    </div>

                    <RenderIf isTrue={operatingInfo.permanentDebtSizingMethod === PermanentDebtSizingMethod.dscr}>
                        <NSCard className="NSCard__left-border--info p-3 mt-2 ProFormaPermanentDebt__form-width">
                            <div className="d-flex align-items-center justify-content-between">
                                <NSLabel className="col-5 pl-0 my-auto font-weight-normal text-white text-nowrap">
                                    Target DSCR
                                </NSLabel>
                                <NSInputNumberStepper
                                    name="targetDscr"
                                    value={operatingInfo.targetDscr != null || operatingInfo.impliedDscr != null ? 
                                        Number((operatingInfo.targetDscr ?? operatingInfo.impliedDscr ?? 0).toFixed(2)) 
                                        : null}
                                    onChange={(value: number) => updateOperationsKeys({ permanentDebt: { targetDscr: value } })}
                                    min={0}
                                    step={0.05}
                                    customSuffix="x"
                                    inputGroupClassName="ProFormaPermanentDebt__input-width ml-n2"
                                />
                            </div>
                        </NSCard>
                    </RenderIf>

                    <RenderIf isTrue={operatingInfo.permanentDebtSizingMethod === PermanentDebtSizingMethod.debt_yield}>
                        <NSCard className="NSCard__left-border--info p-3 mt-2 ProFormaPermanentDebt__form-width">
                            <div className="d-flex align-items-center justify-content-between">
                                <NSLabel className="col-5 pl-0 my-auto font-weight-normal text-white text-nowrap">
                                    Target debt yield
                                </NSLabel>
                                <NSInputNumberStepper
                                    name="targetDebtYield"
                                    value={operatingInfo.targetDebtYield ?? operatingInfo.impliedDebtYield ?? null}
                                    onChange={(value: number) => updateOperationsKeys({ permanentDebt: { targetDebtYield: value } })}
                                    min={0}
                                    step={0.25}
                                    max={100}
                                    percentage
                                    inputGroupClassName="ProFormaPermanentDebt__input-width ml-n2"
                                />
                            </div>
                        </NSCard>
                    </RenderIf>

                    <RenderIf isTrue={operatingInfo.permanentDebtSizingMethod === PermanentDebtSizingMethod.ltv}>
                        <NSCard className="NSCard__left-border--info p-3 mt-2 ProFormaPermanentDebt__form-width">
                            <div className="d-flex align-items-center justify-content-between">
                                <NSLabel className="col-5 pl-0 my-auto font-weight-normal text-white text-nowrap">
                                    Target LTV
                                </NSLabel>
                                <NSInputNumberStepper
                                    name="targetLtv"
                                    value={operatingInfo.targetLtv ?? operatingInfo.impliedLtv ?? null}
                                    onChange={(value: number) => updateOperationsKeys({ permanentDebt: { targetLtv: value } })}
                                    min={0}
                                    step={1}
                                    percentage
                                    inputGroupClassName="ProFormaPermanentDebt__input-width ml-n2"
                                />
                            </div>
                        </NSCard>
                    </RenderIf>
                </Col>
                <Col className="px-1" sm={12} md={6}>
                    <Row className="mx-n1 pt-2">
                        <Col className="px-1 mb-2" sm={12} md={6}>
                            <MetricOutputCard
                                label="Fees & closing costs"
                                value={numeralFormatterCurrency(Math.abs(operatingInfo.feesAndClosingCostsAmount || 0), showCents ? '$0,0.00' : '$0,0')}
                            />
                        </Col>
                        <Col className="px-1 mb-2" sm={12} md={6}>
                            <MetricOutputCard
                                label="Loan payoff"
                                value={`${operatingInfo.loanPayoffDate
                                    ? `${format(parseISO(operatingInfo.loanPayoffDate), 'MMM yyyy')} (Month ${operatingInfo.loanPayoffMonth})` : null}`}
                                showInProgress={!operatingInfo.loanPayoffDate}
                            />
                        </Col>
                        <Col className="px-1 mb-2" sm={12} md={6}>
                            <MetricOutputCard
                                label="Debt service coverage ratio"
                                value={`${numeralFormatterCurrency(operatingInfo.debtServiceCoverageRatio || 0, '0,0.00')}x`}
                                showInProgress={!operatingInfo.debtServiceCoverageRatio}
                            />
                        </Col>
                        <Col className="px-1 mb-2" sm={12} md={6}>
                            <MetricOutputCard
                                label="Cash-on-cash return"
                                value={`${percentFormatter(
                                    (operatingInfo.cashOnCashReturn || 0) * 100,
                                )} (Month ${operatingInfo.cashOnCashReturnMonth})`}
                                showInProgress={!operatingInfo.cashOnCashReturn}
                            />
                        </Col>
                        <Col className="px-1 mb-2" sm={12} md={6}>
                            <MetricOutputCard
                                label="Net equity at refinance"
                                value={numeralFormatterCurrency(operatingInfo.equityDifference || 0, showCents ? '$0,0.00' : '$0,0')}
                                showInProgress={operatingInfo.equityDifference === null}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>

            <div
                className={classNames(['pb-3 pl-3', {
                    'pr-3': !operationBeginIndex,
                }])}
            >
                <div
                    className="ProFormaPermanentDebt__table-wrapper scrollable-container"
                    ref={createRefHandler(tableRef)}
                    onScroll={() => handleTableScroll('table')}
                >
                    <DebtServiceTable
                        baseCashFlowColumns={baseCashFlowColumns}
                    />
                    <BaseTotalsTable
                        tableName="Cash flow after financing"
                        totalAmount={operatingInfo?.cashFlowAfterFinancing?.totalAnnualCashFlow || 0}
                        cashFlowValues={cashFlowAfterFinancingValues}
                        operationBeginIndex={operationBeginIndex}
                        baseCashFlowColumns={baseCashFlowColumns}
                    />
                </div>
            </div>
        </div>
    );
};

export default ProFormaPermanentDebt;
